import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetUploadEndpoint, MakeItemPublic, UploadToS3 } from '../../../../../../connection/uploads';
import { addNewFontPack, loadFontPacks, setSelectedFont } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { CreateFontPack, FontPack, BLAdmin } from '../../../../../../types/working-model';
import Modal from '../../../../../general-ui/modal/modal';
import Font from './font';
import { v4 as uuid } from 'uuid';
import { CreateFont, GetFontName } from '../../../../../../connection/fonts';
import { GetEmptyFontPack } from '../../../../../../store/utils/create-event';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import './fonts.scss';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import LargeButton from '../../../../../general-ui/button/large-button';

export default function Fonts(): JSX.Element {
	const {
		CreateEventReducer: {
			fontPacks,
			workingEvent
		},
		AuthReducer: {
			user,
			token
		}
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const [uploadModalOpen, setUploadModalOpen] = useState(false);

	useEffect(() => {
		dispatch(loadFontPacks(Number(user!.active_channel), token!));
	}, [dispatch, token, user]);

	function selectFont(font: FontPack) {
		dispatch(setSelectedFont(font));

		// ASSISTANT CHECKLIST ITEM: add assistant progress checklist item for changing font
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editFonts)
		) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editFonts));
	}

	const closeModal = useCallback(() => {
		setUploadModalOpen(false);
	}, []);

	const openCustomModal = useCallback(() => {
		setUploadModalOpen(true);
	}, []);

	return (
		<div className="fonts-container">
			{fontPacks.map((font: FontPack) => (
				<Font selected={workingEvent?.settings.design.font?.font_pack === font.font_pack} key={font.font_pack} font={font} onClick={selectFont} />
			))}

			<button className="small" onClick={openCustomModal}><Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Custom Fonts</button>

			<FontUploadModal isOpen={uploadModalOpen} onClose={closeModal} user={user!} />
		</div>
	);
}

interface FontUploadModalProps {
	isOpen: boolean;
	onClose: () => void;
	user: BLAdmin
}

export function FontUploadModal({ isOpen, onClose, user }: FontUploadModalProps): JSX.Element {
	const {
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const [workingFontPack, setWorkingFontPack] = useState<CreateFontPack>(GetEmptyFontPack());
	const dispatch = useDispatch();
	// const headerInput = useRef<HTMLInputElement | null>(null);
	// const bodyInput = useRef<HTMLInputElement | null>(null);
	const [headerUploading, setHeaderUploading] = useState(false);
	const [bodyUploading, setBodyUploading] = useState(false);

	useEffect(() => {
		if (isOpen) { setWorkingFontPack(GetEmptyFontPack()); } //anytime the modal is opened a new empty font pack is loaded
	}, [isOpen]);

	// function openHeaderUpload() {
	// 	headerInput.current?.click();
	// }
	// function openBodyUpload() {
	// 	bodyInput.current?.click();
	// }
	async function headerChange(file: File | FileList) {
		if (file instanceof FileList) return;
		if (file) {
			setHeaderUploading(true);
			const endpoint = await GetUploadEndpoint(token!, `${user!.active_channel}/${uuid()}`, file.name.replace(/[^0-9a-zA-Z.]/g, '-'));
			await UploadToS3(endpoint.url, file, endpoint.fields);
			await MakeItemPublic(token!, endpoint.fields.Key);
			const name = await GetFontName(`${endpoint.url}/${endpoint.fields.Key}`);
			setWorkingFontPack((workingFontPack: any) => ({
				...workingFontPack,
				heading_url: `${endpoint.url}/${endpoint.fields.Key}`,
				heading_name: name.name,
				title: `${name.name} & ${workingFontPack.body_name}`
			}));
			setHeaderUploading(false);
		}
	}
	async function bodyChange(file: File | FileList) {
		if (file instanceof FileList) return;
		if (file) {
			setBodyUploading(true);
			const endpoint = await GetUploadEndpoint(token!, `${user!.active_channel}/${uuid()}`, file.name.replace(/[^0-9a-zA-Z.]/g, '-'));
			await UploadToS3(endpoint.url, file, endpoint.fields);
			await MakeItemPublic(token!, endpoint.fields.Key);
			const name = await GetFontName(`${endpoint.url}/${endpoint.fields.Key}`);
			setWorkingFontPack((workingFontPack: any) => ({
				...workingFontPack,
				body_url: `${endpoint.url}/${endpoint.fields.Key}`,
				body_name: name.name,
				title: `${workingFontPack.heading_name} & ${name.name}`
			}));
			setBodyUploading(false);
		}
	}

	function formValid() {
		return (
			workingFontPack.body_name &&
			workingFontPack.body_url &&
			workingFontPack.heading_name &&
			workingFontPack.heading_url
		);
	}
	async function doneCreatingFont() {
		const created = await CreateFont(token!, workingFontPack);
		dispatch(addNewFontPack(created));
		dispatch(setSelectedFont(created));
		onClose();
	}
	return (
		<Modal
			closeable={false}
			cancellable={true}
			open={isOpen}
			onRequestClose={onClose}
			title={"Add custom fonts"}
			footer={(
				<>
					<button onClick={onClose} style={{ marginRight: 16 }}>Cancel</button>
					<button disabled={!formValid()} onClick={doneCreatingFont} className="primary">Add</button>
				</>
			)}
		>
			<div className="font-upload-modal">
				<p>You can choose from web-safe fonts or upload files of type .ttf, .otf, or .woff</p>

				<div className="font-upload-container">
					<LargeButton
						title="Upload Header Font"
						subtitle="Accepted types are .ttf, .otf, and .woff"
						onFile={headerChange}
						allowedFileTypes={['font/ttf', 'font/otf', 'font/woff']}
						uploading={headerUploading}
					/>

					<LargeButton
						title="Upload Body Font"
						subtitle="Accepted types are .ttf, .otf, and .woff"
						onFile={bodyChange}
						allowedFileTypes={['font/ttf', 'font/otf', 'font/woff']}
						uploading={bodyUploading}
					/>
					{/* <button onClick={openHeaderUpload}>Upload Header</button>
					<input ref={headerInput} onChange={headerChange} type="file" accept=".ttf,.otf,.woff" style={{ display: 'none' }} />

					<button onClick={openBodyUpload}>Upload Body</button>
					<input ref={bodyInput} onChange={bodyChange} type="file" accept=".ttf,.otf,.woff" style={{ display: 'none' }} /> */}

				</div>
			</div>
		</Modal>
	);
}